import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import {
  Activity,
  AlertTriangle,
  Building2,
  CalendarDays,
  Flag,
  Gauge,
  HeartPulse,
  LayoutDashboard,
  Menu,
  Palette,
  Plug,
  Receipt,
  ScrollText,
  Settings,
  Users,
  Workflow,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { BrandMark } from "@/components/brand-mark";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { ImpersonationBanner } from "@/features/super-admin/impersonation-banner";

const sections = [
  {
    title: "Control Plane",
    items: [
      {
        to: "/superadmin",
        label: "Dashboard",
        icon: LayoutDashboard,
        end: true,
      },
      { to: "/superadmin/users", label: "Users", icon: Users },
      { to: "/superadmin/accounts", label: "Accounts", icon: Building2 },
      { to: "/superadmin/streams", label: "Streams", icon: Workflow },
      {
        to: "/superadmin/subscriptions",
        label: "Subscriptions",
        icon: Receipt,
      },
      { to: "/superadmin/usage", label: "Usage / Quotas", icon: Gauge },
      { to: "/superadmin/audit", label: "Audit Log", icon: ScrollText },
    ],
  },
  {
    title: "Operations",
    items: [
      {
        to: "/superadmin/conferences",
        label: "Conferences",
        icon: CalendarDays,
      },
      {
        to: "/superadmin/operations",
        label: "Sync Operations",
        icon: Activity,
      },
      { to: "/superadmin/failures", label: "Failures", icon: AlertTriangle },
      { to: "/superadmin/integrations", label: "Integrations", icon: Plug },
      { to: "/superadmin/jobs", label: "Jobs / Workers", icon: Activity },
      { to: "/superadmin/health", label: "System Health", icon: HeartPulse },
    ],
  },
  {
    title: "Governance",
    items: [
      { to: "/superadmin/flags", label: "Feature Flags", icon: Flag },
      {
        to: "/superadmin/settings",
        label: "Platform Settings",
        icon: Settings,
      },
      { to: "/superadmin/style-guide", label: "Style Guide", icon: Palette },
    ],
  },
];

function PlatformNav() {
  return (
    <nav
      className="flex-1 space-y-4 overflow-y-auto p-3"
      aria-label="Super Admin"
    >
      {sections.map((section) => (
        <div key={section.title}>
          <p className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-rs-ink-subtle">
            {section.title}
          </p>
          <div className="space-y-0.5">
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-rs-sm px-2 py-1.5 text-sm text-rs-ink-muted hover:bg-rs-muted",
                    isActive &&
                      "bg-rs-accent-soft font-medium text-rs-accent-ink",
                  )
                }
              >
                <item.icon className="h-3.5 w-3.5" aria-hidden />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function PlatformSidebarFooter({
  email,
  onSignOut,
}: {
  email?: string | null;
  onSignOut: () => void;
}) {
  return (
    <div className="border-t border-rs-border p-3">
      <p className="truncate text-xs text-rs-ink-muted">{email}</p>
      <Button
        className="mt-2 w-full"
        size="sm"
        variant="secondary"
        onClick={onSignOut}
      >
        Sign Out
      </Button>
    </div>
  );
}

function PlatformBrandHeader() {
  return (
    <div className="border-b border-rs-border px-4 py-4">
      <div className="flex items-center gap-2">
        <BrandMark size="sm" iconVariant="mono" />
        <span className="rounded bg-rs-danger-soft px-1.5 py-0.5 text-[10px] font-bold uppercase text-rs-danger">
          Super Admin
        </span>
      </div>
    </div>
  );
}

export function SuperAdminLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNav, setMobileNav] = useState(false);

  useEffect(() => {
    setMobileNav(false);
  }, [location.pathname]);

  const handleSignOut = () => {
    void signOut().then(() => navigate("/login"));
  };

  return (
    <div className="rs-platform flex min-h-screen flex-col bg-rs-bg">
      <ImpersonationBanner />
      <div className="flex min-h-0 flex-1">
        <aside className="rs-platform-sidebar hidden w-64 shrink-0 border-r border-rs-border bg-rs-sidebar lg:flex lg:flex-col">
          <PlatformBrandHeader />
          <PlatformNav />
          <PlatformSidebarFooter
            email={user?.email}
            onSignOut={handleSignOut}
          />
        </aside>
        {mobileNav ? (
          <div className="fixed inset-0 z-40 flex lg:hidden">
            <div className="rs-platform-sidebar flex w-64 flex-col overflow-y-auto bg-rs-sidebar shadow-rs">
              <div className="flex items-center justify-between gap-2 border-b border-rs-border px-3 py-2">
                <span className="rounded bg-rs-danger-soft px-1.5 py-0.5 text-[10px] font-bold uppercase text-rs-danger">
                  Super Admin
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Close Menu"
                  onClick={() => setMobileNav(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <PlatformNav />
              <PlatformSidebarFooter
                email={user?.email}
                onSignOut={handleSignOut}
              />
            </div>
            <button
              type="button"
              className="flex-1 bg-black/40"
              aria-label="Dismiss Menu"
              onClick={() => setMobileNav(false)}
            />
          </div>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2 border-b border-rs-border bg-rs-elevated px-3 py-2 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Open Menu"
              onClick={() => setMobileNav(true)}
            >
              <Menu className="h-4 w-4" />
            </Button>
            <BrandMark size="sm" iconVariant="mono" />
            <span className="rounded bg-rs-danger-soft px-1.5 py-0.5 text-[10px] font-bold uppercase text-rs-danger">
              Super Admin
            </span>
          </div>
          <main className="min-w-0 flex-1 px-4 py-5 lg:px-6">
            <div className="rs-platform-topbar">
              <span>Super Admin</span>
              <span>Control plane · All accounts</span>
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                Back to Workspace
              </Button>
            </div>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
